import * as ImagePicker from "expo-image-picker";
import { useState } from "react";
import { StyleSheet, View, TouchableOpacity, Image, ActivityIndicator } from "react-native";
import { Text, Icon } from "react-native-paper";

import { MOBILE_DEBUG_VERSION } from "@/lib/debug";
import { logRocketRedactProps } from "@/lib/logrocket";
import { colors } from "@/lib/theme";
import { toJpeg } from "@/utils/resize-image";

interface DocumentStepProps {
    imageUri: string | null;
    onCapture: (uri: string) => void;
    /** Clears the current ID image so the nurse can switch capture method
     *  (e.g. took it with the camera, wants to pick from the library instead). */
    onRemove?: () => void;
}

export function DocumentStep({ imageUri, onCapture, onRemove }: DocumentStepProps) {
    const [picking, setPicking] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [imgLoading, setImgLoading] = useState(false);

    async function handlePick(useCamera: boolean) {
        setPicking(true);
        setError(null);
        console.info(`[mobile ${MOBILE_DEBUG_VERSION}] document pick started`, { useCamera });

        try {
            let result: ImagePicker.ImagePickerResult;
            if (useCamera) {
                const permission = await ImagePicker.requestCameraPermissionsAsync();
                console.info(
                    `[mobile ${MOBILE_DEBUG_VERSION}] document camera permission result`,
                    permission
                );
                if (!permission.granted) {
                    setError("Camera permission required");
                    return;
                }
                result = await ImagePicker.launchCameraAsync({
                    mediaTypes: ["images"],
                    quality: 0.7
                });
            } else {
                result = await ImagePicker.launchImageLibraryAsync({
                    mediaTypes: ["images"],
                    quality: 0.7
                });
            }

            console.info(`[mobile ${MOBILE_DEBUG_VERSION}] document picker result`, {
                canceled: result.canceled,
                assets: result.canceled ? 0 : result.assets.length
            });

            if (result.canceled || result.assets.length === 0) {
                return;
            }
            // Transcode to JPEG before handing off — iOS photo-library IDs are
            // usually HEIC, which the face-compare (Rekognition) step rejects.
            const jpegUri = await toJpeg(result.assets[0].uri);
            onCapture(jpegUri);
        } catch (err) {
            const message =
                err instanceof Error
                    ? err.message
                    : "Image picker could not be opened. Please try again.";
            console.error(`[mobile ${MOBILE_DEBUG_VERSION}] document pick failed`, err);
            setError(message);
        } finally {
            setPicking(false);
        }
    }

    return (
        <View style={styles.container}>
            <View style={styles.header}>
                <Icon source="card-account-details-outline" size={32} color={colors.primary} />
                <Text style={styles.title}>Government ID</Text>
                <Text style={styles.subtitle}>
                    Upload a clear photo of your government-issued photo ID (driver's license,
                    passport, etc.)
                </Text>
            </View>

            {imageUri ? (
                <View style={styles.previewContainer}>
                    {imageUri === "uploaded" ? (
                        <View
                            style={[
                                styles.preview,
                                { alignItems: "center", justifyContent: "center" }
                            ]}
                        >
                            <Icon source="check-circle" size={48} color={colors.success} />
                            <Text
                                style={{ color: colors.textSecondary, marginTop: 8, fontSize: 13 }}
                            >
                                Document uploaded
                            </Text>
                        </View>
                    ) : (
                        // Wrapped so a still-loading remote image shows a spinner
                        // instead of a bare grey box (fixes the "image flickers to
                        // empty" glitch while a viewUrl loads).
                        <View style={styles.preview}>
                            <Image
                                {...logRocketRedactProps()}
                                source={{ uri: imageUri }}
                                style={styles.previewImage}
                                onLoadStart={() => setImgLoading(true)}
                                onLoadEnd={() => setImgLoading(false)}
                            />
                            {imgLoading && (
                                <View style={styles.previewLoading}>
                                    <ActivityIndicator color={colors.primary} />
                                </View>
                            )}
                        </View>
                    )}
                    <View style={styles.actionRow}>
                        <TouchableOpacity
                            style={styles.retakeButton}
                            onPress={() => handlePick(true)}
                            disabled={picking}
                        >
                            <Icon source="camera-retake" size={18} color={colors.primary} />
                            <Text style={styles.retakeText}>Re-capture</Text>
                        </TouchableOpacity>
                        {onRemove && imageUri !== "uploaded" && (
                            <TouchableOpacity
                                style={styles.removeButton}
                                onPress={onRemove}
                                disabled={picking}
                            >
                                <Icon source="close" size={18} color={colors.textSecondary} />
                                <Text style={styles.removeText}>Remove</Text>
                            </TouchableOpacity>
                        )}
                    </View>
                </View>
            ) : (
                <View style={styles.buttonRow}>
                    <TouchableOpacity
                        style={styles.pickButton}
                        onPress={() => handlePick(true)}
                        disabled={picking}
                    >
                        {picking ? (
                            <ActivityIndicator size="small" color="#FFFFFF" />
                        ) : (
                            <>
                                <Icon source="camera" size={20} color="#FFFFFF" />
                                <Text style={styles.pickButtonText}>Camera</Text>
                            </>
                        )}
                    </TouchableOpacity>
                    <TouchableOpacity
                        style={[styles.pickButton, styles.pickButtonSecondary]}
                        onPress={() => handlePick(false)}
                        disabled={picking}
                    >
                        <Icon source="image-outline" size={20} color={colors.primary} />
                        <Text style={[styles.pickButtonText, { color: colors.primary }]}>
                            Gallery
                        </Text>
                    </TouchableOpacity>
                </View>
            )}

            {error && <Text style={styles.errorText}>{error}</Text>}
        </View>
    );
}

const styles = StyleSheet.create({
    container: { flex: 1, gap: 20 },
    header: { alignItems: "center", gap: 8, paddingTop: 12 },
    title: { fontSize: 20, fontWeight: "700", color: colors.textPrimary },
    subtitle: {
        fontSize: 14,
        color: colors.textSecondary,
        textAlign: "center",
        paddingHorizontal: 24
    },
    previewContainer: { alignItems: "center", gap: 12 },
    preview: {
        width: "90%",
        height: 220,
        borderRadius: 12,
        backgroundColor: colors.backgroundSecondary,
        alignSelf: "center",
        overflow: "hidden"
    },
    previewImage: { width: "100%", height: "100%" },
    previewLoading: {
        ...StyleSheet.absoluteFill,
        alignItems: "center",
        justifyContent: "center"
    },
    actionRow: { flexDirection: "row", gap: 10, alignItems: "center" },
    removeButton: {
        flexDirection: "row",
        alignItems: "center",
        gap: 6,
        paddingVertical: 10,
        paddingHorizontal: 20,
        borderRadius: 10,
        borderWidth: 1,
        borderColor: colors.border
    },
    removeText: { fontSize: 14, fontWeight: "600", color: colors.textSecondary },
    retakeButton: {
        flexDirection: "row",
        alignItems: "center",
        gap: 6,
        paddingVertical: 10,
        paddingHorizontal: 20,
        borderRadius: 10,
        borderWidth: 1,
        borderColor: colors.primary
    },
    retakeText: { fontSize: 14, fontWeight: "600", color: colors.primary },
    buttonRow: { flexDirection: "row", gap: 10, paddingHorizontal: 24 },
    pickButton: {
        flex: 1,
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "center",
        gap: 8,
        backgroundColor: colors.primary,
        borderRadius: 12,
        paddingVertical: 48
    },
    pickButtonSecondary: { backgroundColor: colors.primaryLight },
    pickButtonText: { fontSize: 16, fontWeight: "700", color: "#FFFFFF" },
    errorText: { fontSize: 12, color: colors.error, textAlign: "center" }
});
